import React, { useRef, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import "./itemCrousel.css";

function ItemCrousel(props) {
  const carouselRef = useRef(null);
  const [id] = useState(() => `carousel-${uuidv4().substring(0, 8)}`);
  const [isScrolling, setIsScrolling] = useState(false);
  const [visibleItems, setVisibleItems] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  let div_styles = {};
  let img_styles = {};
  let item_styles = {};
  let cloth_area_styles = {};
  let labelStyle = {};

  // Define styles based on size prop
  if (props.size === "smaller") {
    div_styles = { height: "288px" };
    img_styles = { height: "184px" };
    item_styles = { width: "148px" };
    cloth_area_styles = { position: "relative" };
  } else if (props.size === "larger") {
    div_styles = { height: "max-content" };
    img_styles = { height: "333px" };
    item_styles = {
      height: "340px",
      width: "222px",
      minWidth: "222px"
    };
    cloth_area_styles = { height: "390px", position: "relative" };
  } else if (props.size === "largest") {
    div_styles = {
      height: "max-content",
      maxWidth: "100%",
      padding: "30px 40px",
      width: "100%"
    };
    img_styles = { height: "417px" };
    item_styles = {
      height: "461px",
      width: "302px",
      minWidth: "302px"
    };
    cloth_area_styles = { height: "470px", position: "relative" };

    labelStyle = {
      fontSize: "14px",
      fontWeight: "500",
      padding: "0 0 30px 25px"
    };
  }

  const data = props.data || [];

  // Calculate visible items and item width on mount and resize
  useEffect(() => {
    function calculateItemDimensions() {
      if (!carouselRef.current) return;
      
      const containerWidth = carouselRef.current.clientWidth;
      const containerScrollWidth = carouselRef.current.scrollWidth;
      const firstItem = carouselRef.current.querySelector('.item');
      
      if (firstItem) {
        const itemFullWidth = firstItem.offsetWidth;
        // Include margins in width calculation
        const computedStyle = window.getComputedStyle(firstItem);
        const marginLeft = parseInt(computedStyle.marginLeft || 0);
        const marginRight = parseInt(computedStyle.marginRight || 0);
        const totalItemWidth = itemFullWidth + marginLeft + marginRight;
        
        // Calculate how many items can fit completely
        const visibleCount = Math.floor(containerWidth / totalItemWidth);
        
        setVisibleItems(Math.max(1, visibleCount));
        setItemWidth(totalItemWidth);
        setMaxScroll(containerScrollWidth - containerWidth);
      }
    }

    calculateItemDimensions();
    
    // Add resize listener with debounce
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateItemDimensions, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (carouselRef.current) {
        setScrollPosition(carouselRef.current.scrollLeft);
        // Update max scroll value in case content changed
        setMaxScroll(carouselRef.current.scrollWidth - carouselRef.current.clientWidth);
      }
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, []);

  function scrollLeft() {
    if (isScrolling || !carouselRef.current) return;
    setIsScrolling(true);
    
    // Calculate scroll amount as one full item width to ensure perfect alignment
    const scrollAmount = -1 * itemWidth;
    // Ensure we don't scroll past the beginning
    const newPosition = Math.max(0, scrollPosition + scrollAmount);
    
    carouselRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
    
    setTimeout(() => setIsScrolling(false), 500);
  }

  function scrollRight() {
    if (isScrolling || !carouselRef.current) return;
    setIsScrolling(true);
    
    // Calculate scroll amount as one full item width to ensure perfect alignment
    const scrollAmount = itemWidth;
    // Ensure we don't scroll past the end
    const newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
    
    carouselRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
    
    setTimeout(() => setIsScrolling(false), 500);
  }

  // Determine if arrows should be visible
  const showLeftArrow = scrollPosition > 5; // Add a small threshold
  const showRightArrow = maxScroll > 5 && scrollPosition < maxScroll - 5; // Add small thresholds

  // Arrow styles to ensure proper positioning
  const arrowStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 10,
    backgroundColor: "white",
    borderRadius: "50%",
    border: "none",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    opacity: isHovering ? 1 : 0,
    transition: "opacity 0.3s ease",
    fontSize: "18px",
    fontWeight: "bold",
    color: "#333"
  };
  
  const leftArrowStyle = {
    ...arrowStyle,
    left: "8px"
  };
  
  const rightArrowStyle = {
    ...arrowStyle,
    right: "8px"
  };

  return (
    <div style={div_styles} className="items_crousel">
      {props.label && (
        <div style={labelStyle} className="label">
          <h1 className="label_text">{props.label}</h1>
        </div>
      )}
      <div 
        style={cloth_area_styles} 
        className="cloth_area"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {showLeftArrow && (
          <button 
            onClick={scrollLeft} 
            style={leftArrowStyle}
            aria-label="Scroll left"
            className="arrow-button"
            disabled={isScrolling}
          >
            ‹
          </button>
        )}
        
        <div 
          id={id} 
          ref={carouselRef} 
          className="items_area scroll-smooth hide-scrollbar"
          style={{
            scrollSnapType: 'x mandatory',
            padding: '0 8px', // Add slight padding to ensure first/last items are fully visible
            boxSizing: 'border-box',
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            width: '100%'
          }}
        >
          {data.slice(0, 30).map((item, index) => (
            <Item
              key={item._id || index}
              _id={item._id}
              src={item.image && item.image.length > 0 ? item.image[0].src : ''}
              secondaryImg={item.image && item.image.length > 1 ? item.image[1].src : ''}
              styles={img_styles}
              name={item.title}
              item_styles={{
                ...item_styles,
                scrollSnapAlign: 'start',
                flexShrink: 0,  // Prevent items from shrinking
                margin: '0 8px'  // Add consistent margins
              }}
              category={item.category}
              price={item.price}
              swatches={item.swatches || []}
              code={item.articleCode}
              slug={item.slug}
              image={item.image || []}
            />
          ))}
        </div>

        {showRightArrow && (
          <button 
            onClick={scrollRight} 
            style={rightArrowStyle}
            aria-label="Scroll right"
            className="arrow-button"
            disabled={isScrolling}
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
}

function Item(props) {
  const primaryImg = props.image && props.image.length > 0 && props.image[0].dataAltImage 
                      ? props.image[0].dataAltImage 
                      : props.src || '';                      
  const secondaryImg = props.image && props.image.length > 1 && props.image[1].src 
                      ? props.image[1].src 
                      : props.src || '';

  const [img, setImg] = useState(primaryImg);
  const [isLoading, setIsLoading] = useState(true);
  
  function handleImageLoad() {
    setIsLoading(false);
  }

  function handleImageError() {
    setIsLoading(false);
    // Could set a fallback image here if needed
  }

  // Text ellipsis styles
  const textEllipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    display: "block"
  };

  return (
    <a 
      href={`/productpage/${props.slug || ''}`} 
      style={props.item_styles} 
      className="item relative overflow-hidden"
      onMouseEnter={() => setImg(secondaryImg)}
      onMouseLeave={() => setImg(primaryImg)}
    >
      <div className="relative overflow-hidden" style={{ height: props.styles.height }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
          </div>
        )}
        
        <img 
          style={{ 
            ...props.styles,
            transition: "all 0.4s ease",
            objectFit: "cover",
            width: "100%"
          }} 
          src={img} 
          alt={props.name || "Product image"}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
      
      <div className="mt-2">
        {/* <p style={textEllipsisStyle} title={props.name}>{props.name || "Unnamed product"}</p> */}
        <p className="smol" style={{marginTop: "4px"}}>{props.price || ""}</p>
        
        {/* {props.swatches && props.swatches.length > 0 && (
          <div className="colors mt-2 flex">
            {props.swatches.map((swatch, index) => (
              <Color
                key={index}
                color={swatch.colorCode || "#ccc"}
              />
            ))}
          </div>
        )} */}
      </div>
    </a>
  );
}

function Color(props) {
  const styles = {
    backgroundColor: props.color || "#ccc",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    margin: "0 4px 0 0",
    border: "1px solid #eee"
  };
  
  return (
    <div style={styles} className="color_div" title={props.color || "Color option"}></div>
  );
}

export default ItemCrousel;